import {
  Button as HeadlessButton,
  type ButtonProps as HeadlessButtonProps,
} from "@headlessui/react";
import clsx from "clsx";
import React from "react";
import { twMerge } from "tailwind-merge";

export type ButtonType = "plain" | "outline" | "clear";
export type ButtonStyle = "plain" | "error";
export type ButtonSize = "xs" | "sm" | "md" | "lg" | "xl";

export type ButtonProps = Omit<
  HeadlessButtonProps,
  "onClick" | "children" | "className"
> & {
  buttonType?: ButtonType;
  buttonStyle?: ButtonStyle;
  size?: ButtonSize;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  children?: React.ReactNode;
  // Link-specific props
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: "min-h-7.5 gap-1 px-2.5 py-1 text-sm",
  sm: "min-h-9.5 gap-1 px-3.5 py-2 text-sm",
  md: "min-h-10.5 gap-1 px-4 py-2.5 text-sm",
  lg: "min-h-11.5 gap-1.5 px-[18px] py-2.5 text-md",
  xl: "min-h-12.5 gap-1.5 px-5 py-3 text-md",
};

function buttonClassFromType(
  buttonType: ButtonType,
  buttonStyle: ButtonStyle,
  disabled?: boolean,
) {
  const isError = buttonStyle === "error";

  if (buttonType === "plain") {
    if (disabled) {
      return clsx("border border-bg-disabled bg-disabled text-fg-disabled");
    }
    return clsx(
      "border border-white/12 text-white",
      isError
        ? "bg-error-solid hover:bg-error-700"
        : "bg-brand-solid hover:bg-brand-solid_hover",
    );
  }

  if (buttonType === "outline") {
    if (disabled) {
      return "border border-border-disabled_subtle bg-primary text-fg-disabled";
    }
    return clsx(
      "border bg-primary",
      isError
        ? "border-border-error_subtle text-error-primary hover:bg-error-primary hover:text-error-primary_hover"
        : "border-border-primary text-secondary hover:bg-primary_hover hover:text-secondary_hover",
    );
  }

  if (disabled) {
    return "border border-transparent text-fg-disabled";
  }
  return clsx(
    "border border-transparent",
    isError
      ? "text-error-primary hover:bg-error-primary hover:text-error-primary_hover"
      : "text-tertiary hover:bg-primary_hover hover:text-tertiary_hover",
  );
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      buttonType = "plain",
      buttonStyle = "plain",
      size = "md",
      disabled,
      className,
      children,
      href,
      target,
      rel,
      ...props
    },
    ref,
  ) => {
    const buttonClass = twMerge(
      "inline-flex items-center justify-center rounded-full font-mono font-medium transition-colors",
      sizeClasses[size],
      disabled ? "" : "cursor-pointer",
      buttonClassFromType(buttonType, buttonStyle, disabled),
      className,
    );

    if (href !== undefined) {
      return (
        <a
          href={href}
          target={target}
          rel={rel ?? (target === "_blank" ? "noopener noreferrer" : undefined)}
          className={buttonClass}
          {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {children}
        </a>
      );
    }

    return (
      <HeadlessButton
        ref={ref}
        className={buttonClass}
        disabled={Boolean(disabled)}
        {...props}
      >
        {children}
      </HeadlessButton>
    );
  },
);

Button.displayName = "Button";
